import { LONG_DASH } from '@/constants/stringVars';
import { text } from '@/theme/colors/palettes';
import { TCortexOrganizationInsightsV1, TCortexRecommendationV1 } from '@/types/Cortex.types';
import { cortexPriorityColor, orderCortexRecommendationsV1 } from '@/utils/cortexInsights';
import { capitalize } from '@/utils/stringUtils';
import { HStack, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import ExpandableText from '@/components/atoms/ExpandableText';

// Legacy V1 recommendation card — `priority` (left, colored) / `category` + `timeline`
// (right, muted) / `action` (body) / `— expectedImpact` (muted blue). When the monolith
// stops emitting V1 cached snapshots this file can be deleted along with the V1 types.

interface CardProps {
  recommendation: TCortexRecommendationV1;
  largerFont?: boolean;
  // When false, the body text always renders in full (no "Read more" toggle). Defaults to true.
  expandable?: boolean;
}

export const RecommendationCardV1 = ({ recommendation, largerFont, expandable = true }: CardProps) => {
  const priorityColor = cortexPriorityColor(recommendation.priority);
  const fontSize = largerFont ? '16px' : '14px';

  return (
    <VStack
      alignItems={'start'}
      gap={largerFont ? 2 : 1.5}
      width={'100%'}
      backgroundColor={'background.lightBlue'}
      borderRadius={'10px'}
      padding={'14px 16px'}
    >
      <HStack width={'100%'} justifyContent={'space-between'}>
        <Text variant={'urbanistBold'} color={priorityColor} fontSize={fontSize}>
          {capitalize((recommendation.priority ?? '').toLowerCase())}
        </Text>
        <HStack gap={'8px'}>
          {recommendation.category ? (
            <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumGray'}>
              {recommendation.category.toUpperCase()}
            </Text>
          ) : null}
          {recommendation.timeline ? (
            <Text variant={'urbanistExtraBoldSmall'} color={'text.mediumBlue'}>
              {recommendation.timeline.toUpperCase()}
            </Text>
          ) : null}
        </HStack>
      </HStack>
      {expandable ? (
        <ExpandableText text={recommendation.action} variant={'urbanistBold'} fontSize={fontSize} />
      ) : (
        <Text variant={'urbanistBold'} fontSize={fontSize}>
          {recommendation.action}
        </Text>
      )}
      {recommendation.expectedImpact ? (
        <Text variant={'urbanistMedium'} color={text.mediumBlue} fontSize={fontSize}>
          {LONG_DASH} {capitalize(recommendation.expectedImpact)}
        </Text>
      ) : null}
    </VStack>
  );
};

interface Props {
  insights: TCortexOrganizationInsightsV1;
}

const RecommendationsDisplayV1 = ({ insights }: Props) => {
  const recommendations = orderCortexRecommendationsV1(insights);

  if (recommendations.length === 0) {
    return (
      <Text variant={'urbanistMediumRegular'} color={'text.mediumBlue'} marginBottom={'20px'}>
        No recommendations available yet.
      </Text>
    );
  }

  return (
    <VStack gap={'12px'} width={'100%'}>
      {recommendations.map((recommendation, i) => (
        <RecommendationCardV1 key={`${recommendation.category}-${i}`} recommendation={recommendation} />
      ))}
    </VStack>
  );
};

export default RecommendationsDisplayV1;
